import React, { useState, useContext, useEffect, useRef } from 'react';
import { useCuringEstimate } from '../../api/curing';
import CuringControl from './CuringControl';
import DroughtFactorControl from './DroughtFactorControl';
import DroughtIndexControl from './DroughtIndexControl';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  BookmarkAdd as BookmarkAddIcon,
} from '@mui/icons-material';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useSimulation, SimulationParams } from '../../contexts/SimulationContext';
import { IgnitionRegimeContext } from '../../contexts/IgnitionRegimeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWeather, ApiWeather } from '../../api/weather';
import { useCreateWeatherPreset } from '../../api/weatherPresets';
import WeatherChart, { type RangeSeries } from '../WeatherChart';
import WeatherPointEditor, { WeatherPointValues } from './WeatherPointEditor';
import SpottingModeSelector from './SpottingModeSelector';
import { SpottingMode } from '../../types/spotting';
import dayjs from 'dayjs';
import { transposeWeatherTimestamps, transposeTimestampList } from '../../utils/datetime';
import { weatherGridLabelSuffix, weatherGridPointCount } from '../../utils/weatherGrid';
import type { WeatherGrid } from '../../types/weatherGrid';

interface RunDateViewProps {
  projectId: string;
  onGenerate?: (params?: SimulationParams) => void;
  onClose?: () => void;
  presetValues?: {
    droughtFactor?: number;
    droughtIndex?: number | null;
    hour?: number;
    duration?: number;
    curing?: number;
    spottingMode?: SpottingMode;
    weatherOverrides?: Record<string, WeatherPointValues>;
  };
  weatherDate: dayjs.Dayjs;
  projectDate: dayjs.Dayjs;
}

const RunDateView: React.FC<RunDateViewProps> = ({ projectId, onGenerate, onClose, presetValues, weatherDate, projectDate }) => {
  const { selectedTimezone } = useTimezone();
  const { simulationParams, setSimulationParams } = useSimulation();
  const { selectedRegime } = useContext(IgnitionRegimeContext);
  const { user } = useAuth();
  const unitSystem = useUnitSystem();

  // Check if user has longrange_simulation capability
  const hasLongRangeCapability = user?.is_staff || user?.capabilities?.some(
    (cap) => cap.codename === 'longrange_simulation'
  );
  const maxDuration = hasLongRangeCapability ? 336 : 48;

  const [duration, setDuration] = useState(simulationParams?.duration || 8);
  const [startHour, setStartHour] = useState(10);
  const [isGenerating, setIsGenerating] = useState(false);
  const [droughtFactor, setDroughtFactor] = useState<number>(simulationParams?.droughtFactor ?? 10);
  const [droughtIndex, setDroughtIndex] = useState<number | null>(simulationParams?.droughtIndex ?? null);
  // True while the index shown is the forecast's own value for this date.
  const [droughtIndexFromForecast, setDroughtIndexFromForecast] = useState(false);
  // simulationParams.curing may be the string "auto" (meaning "use the
  // satellite reading"); the slider itself always holds a number.
  const [curing, setCuring] = useState<number>(
    typeof simulationParams?.curing === 'number' ? simulationParams.curing : 100,
  );
  // Whether the satellite reading is in use, or the user has taken over.
  const [usingCuringEstimate, setUsingCuringEstimate] = useState(true);
  const [spottingMode, setSpottingMode] = useState<SpottingMode>(simulationParams?.spottingMode ?? 'realistic');

  const [weatherOverrides, setWeatherOverrides] = useState<Record<string, WeatherPointValues>>({});
  const [selectedPoint, setSelectedPoint] = useState<{ timestamp: string; values: WeatherPointValues; anchorEl?: Element } | null>(null);
  const skipOverrideResetRef = useRef(false);

  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetSnackbar, setPresetSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const createPresetMutation = useCreateWeatherPreset();

  // Apply preset values when they change
  useEffect(() => {
    if (!presetValues) return;
    skipOverrideResetRef.current = true;
    if (presetValues.hour !== undefined) {
      setStartHour(presetValues.hour);
    }
    if (presetValues.droughtFactor !== undefined) {
      setDroughtFactor(presetValues.droughtFactor);
    }
    if (presetValues.droughtIndex !== undefined) {
      setDroughtIndex(presetValues.droughtIndex);
      setDroughtIndexFromForecast(false);
    }
    if (presetValues.duration !== undefined) {
      setDuration(presetValues.duration);
    }
    if (presetValues.curing !== undefined) {
      setCuring(presetValues.curing);
    }
    if (presetValues.spottingMode !== undefined) {
      setSpottingMode(presetValues.spottingMode);
    }
    if (presetValues.weatherOverrides !== undefined) {
      setWeatherOverrides(presetValues.weatherOverrides);
    }
  }, [presetValues]);

  const startDateTime = dayjs
    .tz(weatherDate.format('YYYY-MM-DD'), selectedTimezone)
    .hour(startHour)
    .minute(0)
    .second(0)
    .millisecond(0);

  // `weatherGrid`/`spread` ride along with the fetched day: the query cache
  // is the state for all three, so they can never drift from each other.
  // Which series gets its across-project range shaded (one at a time; off by default).
  const [rangeSeries, setRangeSeries] = useState<RangeSeries>("none");
  const { weather: apiWeather, weatherGrid, spread }: ApiWeather = useWeather(
    'USE_WEATHER_FORECAST',
    projectId,
    startDateTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
    duration,
    25,
    35,
    10,
    30,
    70,
    0,
    true
  );

  useEffect(() => {
    if (apiWeather?.data?.drought_factor != null) {
      setDroughtFactor(apiWeather.data.drought_factor);
      setDroughtIndex(apiWeather.data.drought_index ?? null);
      setDroughtIndexFromForecast(apiWeather.data.drought_index != null);
      return;
    }

    if (apiWeather !== undefined) {
      setDroughtFactor(10);
      setDroughtIndex(null);
      setDroughtIndexFromForecast(false);
    }
  }, [apiWeather]);

  useEffect(() => {
    if (skipOverrideResetRef.current) {
      skipOverrideResetRef.current = false;
      return;
    }
    setWeatherOverrides({});
    setSelectedPoint(null);
  }, [weatherDate, startHour, duration]);

  const handlePointClick = (timestamp: string, raw: { wind_speed: number; wind_direction: number; temperature: number; relative_humidity: number }, event: MouseEvent) => {
    const current = weatherOverrides[timestamp] ?? {
      temperature: raw.temperature,
      relative_humidity: raw.relative_humidity,
      wind_speed: raw.wind_speed,
      wind_direction: raw.wind_direction,
    };
    setSelectedPoint({ timestamp, values: current, anchorEl: event.target as Element });
  };

  const handleApplyOverride = (timestamp: string, values: WeatherPointValues) => {
    setWeatherOverrides((prev) => ({ ...prev, [timestamp]: values }));
    setSelectedPoint((prev) => prev ? { ...prev, timestamp, values } : null);
  };

  const handleResetOverride = (timestamp: string) => {
    setWeatherOverrides((prev) => {
      const next = { ...prev };
      delete next[timestamp];
      return next;
    });
    setSelectedPoint(null);
  };

  const mergedWeather = (() => {
    const base = apiWeather?.data?.interval_weather;
    if (!base) return base;
    const result: typeof base = { ...base };
    for (const [ts, overrideVals] of Object.entries(weatherOverrides)) {
      if (result[ts]) {
        result[ts] = {
          ...result[ts],
          temperature: overrideVals.temperature,
          relative_humidity: overrideVals.relative_humidity,
          wind_speed: overrideVals.wind_speed,
          wind_direction: overrideVals.wind_direction,
        };
      }
    }
    return result;
  })();

  // Curing is resolved against the date being simulated, not today: a run for
  // next week wants what the estimate says about next week.
  const simulationDateISO = projectDate.format('YYYY-MM-DD');
  const { data: curingData } = useCuringEstimate(projectId, simulationDateISO);
  const curingForDate = curingData?.available ? curingData.for_date : undefined;

  useEffect(() => {
    if (!curingForDate) return;
    if (curingForDate.within_window && curingForDate.curing !== null) {
      // A measurement covers this date; adopt it unless the user has taken over.
      if (usingCuringEstimate) setCuring(curingForDate.curing);
    } else {
      // Nothing covers it. Start from a typical value for the time of year and
      // hand the decision to the user rather than reusing a stale reading.
      setUsingCuringEstimate(false);
      if (curingForDate.suggested !== null && curingForDate.suggested !== undefined) {
        setCuring(curingForDate.suggested);
      }
    }
  }, [curingForDate, usingCuringEstimate]);


  const FORECAST_MAX_FUTURE_DAYS = 14;
  const earliestArchiveDate = dayjs('1940-01-01');
  const latestForecastDate = dayjs().add(FORECAST_MAX_FUTURE_DAYS, 'day');
  const earliestArchiveBoundary = earliestArchiveDate.startOf('day');
  const latestForecastBoundary = latestForecastDate.endOf('day');
  const simulationEndTime = startDateTime.add(duration, 'hour');
  const hasValidForecast =
    startDateTime.valueOf() >= earliestArchiveBoundary.valueOf() &&
    simulationEndTime.valueOf() <= latestForecastBoundary.valueOf();

  const handleGenerate = async () => {
    if (!simulationParams) return;

    setIsGenerating(true);

    const simulationTime = dayjs(projectDate)
      .hour(startHour)
      .minute(0)
      .second(0)
      .millisecond(0);

    const simulationTimeTz = simulationTime.tz(selectedTimezone, true);

    // Transpose weather timestamps from the weather date onto the project date
    // so the hourly conditions align with the project's landscape
    const transposedWeather = transposeWeatherTimestamps(
      mergedWeather,
      weatherDate,
      projectDate,
    );

    // The grid's values are sent untouched (per-hour overrides live in
    // `weather`; the backend applies them as deltas to every lattice point),
    // but its timestamps must stay string-equal to the transposed weather
    // keys, so they get the same day shift.
    const transposedGrid: WeatherGrid | null = weatherGrid
      ? { ...weatherGrid, timestamps: transposeTimestampList(weatherGrid.timestamps, weatherDate, projectDate) }
      : null;

    const nextParams: SimulationParams = {
      ...simulationParams,
      simulationType: 'USE_WEATHER_FORECAST',
      droughtFactor: droughtFactor,
      droughtIndex,
      // Send "auto" while the satellite reading is in use so the run is
      // recorded as using a measurement rather than a typed-in number.
      curing: usingCuringEstimate && curingForDate?.within_window ? 'auto' : curing,
      interval: (duration * 60) / 240,
      duration: duration,
      startTime: simulationTimeTz.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      timezone: selectedTimezone,
      weather: transposedWeather || {},
      fireSpreadModel: 'New',
      spottingMode,
      ignitionRegimeId: selectedRegime?.id || null,
      weatherLabel: `${weatherDate.format('MMM D, YYYY')} weather${weatherGridLabelSuffix(transposedGrid)}`,
      weatherDate: weatherDate.format('YYYY-MM-DD'),
      // Omitted (not null) when the project is too small for a lattice, so the
      // POST body carries no `weather_grid` key at all in that case.
      weather_grid: transposedGrid ?? undefined,
    };

    setSimulationParams(nextParams);

    // Pass the freshly-built params directly so the generated sim uses the
    // conditions just selected rather than racing the state update above.
    await onGenerate?.(nextParams);
    setIsGenerating(false);
    onClose?.();
  };

  const handleSavePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    try {
      await createPresetMutation.mutateAsync({
        name,
        preset_type: 'date',
        params: {
          date: weatherDate.format('YYYY-MM-DD'),
          hour: startHour,
          duration,
          drought_factor: droughtFactor,
          drought_index: droughtIndex,
          curing,
          spotting_mode: spottingMode,
          weather_overrides: Object.keys(weatherOverrides).length > 0 ? weatherOverrides : undefined,
        },
      });
      setPresetDialogOpen(false);
      setPresetSnackbar({ open: true, message: `Preset "${name}" saved!`, severity: 'success' });
    } catch {
      setPresetSnackbar({ open: true, message: 'Failed to save preset', severity: 'error' });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
          Run Forecast Weather
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Select a date and time to run a simulation using historical weather data or forecast conditions.
        </Typography>
      </Box>

      {/* Time Selection */}
      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Start Time</InputLabel>
              <Select
                value={startHour}
                onChange={(e: SelectChangeEvent<number>) => setStartHour(Number(e.target.value))}
                label="Start Time"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Duration</InputLabel>
              <Select
                value={duration}
                onChange={(e: SelectChangeEvent<number>) => setDuration(Number(e.target.value))}
                label="Duration"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                {Array.from(
                  { length: Math.floor((maxDuration - 8) / 2) + 1 },
                  (_, i) => 8 + i * 2
                ).map((hours) => (
                  <MenuItem key={hours} value={hours}>
                    {hours} hrs {hours > 48 ? `(${Math.floor(hours / 24)}d ${hours % 24}h)` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Drought Factor, Curing and Drought Index side by side */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 5 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <DroughtFactorControl value={droughtFactor} onChange={setDroughtFactor} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <CuringControl
              value={curing}
              onChange={(next) => { setUsingCuringEstimate(false); setCuring(next); }}
              forDate={curingForDate}
              usingEstimate={usingCuringEstimate}
              onUseEstimate={(use) => {
                setUsingCuringEstimate(use);
                if (use && curingForDate?.curing !== null && curingForDate?.curing !== undefined) {
                  setCuring(curingForDate.curing);
                }
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <DroughtIndexControl
              value={droughtIndex}
              onChange={(mm) => { setDroughtIndex(mm); setDroughtIndexFromForecast(false); }}
              unitSystem={unitSystem}
              source={droughtIndexFromForecast
                ? 'From the forecast for this date. Drag to test a drier or wetter landscape.'
                : null}
            />
          </Box>
        </Box>

        {/* Spotting */}
        <Box sx={{ mb: 3 }}>
          <SpottingModeSelector value={spottingMode} onChange={setSpottingMode} />
        </Box>

        {/* Weather Chart */}
        {hasValidForecast && apiWeather?.data?.interval_weather && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
              Click any point to edit weather conditions for that hour
            </Typography>
            <WeatherChart
              data={mergedWeather ?? apiWeather.data.interval_weather}
              spread={spread}
              rangeSeries={weatherGrid ? rangeSeries : "none"}
              onPointClick={handlePointClick}
              overriddenTimestamps={new Set(Object.keys(weatherOverrides))}
              selectedTimestamp={selectedPoint?.timestamp ?? null}
            />
            {weatherGrid && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                  Chart shows the project centre; weather is sampled at {weatherGridPointCount(weatherGrid)} points
                  ({weatherGrid.shape[0]}×{weatherGrid.shape[1]}, ~{Math.round(weatherGrid.spacing_km)} km apart).
                  Hover for the range across the project, or shade it:
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={rangeSeries}
                  onChange={(_e, v: RangeSeries | null) => { if (v) setRangeSeries(v); }}
                  aria-label="Shade range across project"
                  sx={{
                    '& .MuiToggleButton-root': {
                      py: 0, px: 1, fontSize: '0.7rem', textTransform: 'none',
                      color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.15)',
                    },
                    '& .Mui-selected': { color: '#fff' },
                  }}
                >
                  <ToggleButton value="none">Off</ToggleButton>
                  <ToggleButton value="temperature">Temp</ToggleButton>
                  <ToggleButton value="humidity">Humidity</ToggleButton>
                  <ToggleButton value="wind">Wind</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>
        )}

        {!hasValidForecast && (
          <Box
            sx={{
              p: 3,
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: '#ff6b6b' }}>
              Weather forecast is not available for the selected date range
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Please select a date between {earliestArchiveDate.format('DD/MM/YYYY')} and{' '}
              {latestForecastDate.format('DD/MM/YYYY')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<BookmarkAddIcon />}
          onClick={() => { setPresetName(''); setPresetDialogOpen(true); }}
          disabled={!hasValidForecast || !apiWeather?.data?.interval_weather}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'rgba(255, 255, 255, 0.7)',
            py: 2,
            '&:hover': { borderColor: '#fff', color: '#fff' },
            '&:disabled': { borderColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.3)' },
          }}
        >
          Save Preset
        </Button>
        <Button
          variant="contained"
          size="large"
          sx={{
            flex: 1,
            bgcolor: '#ff6b35',
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            '&:hover': { bgcolor: '#ff7f50' },
            '&:disabled': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.3)' },
          }}
          startIcon={isGenerating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
          onClick={handleGenerate}
          disabled={!hasValidForecast || isGenerating || !apiWeather?.data?.interval_weather}
        >
          {isGenerating ? 'GENERATING...' : 'GENERATE SIMULATION'}
        </Button>
      </Box>

      {/* Create Preset Dialog */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#fff' }}>Save as Preset</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Preset name"
            fullWidth
            size="small"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
            sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }, '&.Mui-focused fieldset': { borderColor: '#ff6b35' } }, '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)', '&.Mui-focused': { color: '#ff6b35' } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim() || createPresetMutation.isPending} sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar open={presetSnackbar.open} autoHideDuration={3000} onClose={() => setPresetSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={presetSnackbar.severity} onClose={() => setPresetSnackbar(p => ({ ...p, open: false }))}>
          {presetSnackbar.message}
        </Alert>
      </Snackbar>

      {selectedPoint && (
        <WeatherPointEditor
          anchorEl={selectedPoint.anchorEl}
          timestamp={selectedPoint.timestamp}
          values={selectedPoint.values}
          isOverridden={!!weatherOverrides[selectedPoint.timestamp]}
          onApply={handleApplyOverride}
          onReset={handleResetOverride}
          onClose={() => setSelectedPoint(null)}
        />
      )}
    </Box>
  );
};

export default RunDateView;
